import React from "react";
import { COMPANIES_MODULE_HIDDEN } from "@/modules/companies/manifest";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FileText, Home, LayoutGrid, Mail, Plus, Star, User } from "lucide-react";
import { isViewerMember } from "@/utils/entityType";
import { cx } from "@/components/ui";

// Screens where the bar would get in the way: sign-in, registration and the subscription steps.
const HIDDEN_PREFIXES = [
  "/login", "/register", "/registration", "/subscribe", "/pay", "/payment", "/bank-transfer",
  "/vat", "/declaration", "/field-of-work-classification", "/launch", "/support",
];

/**
 * Four-destination bottom bar for phones and tablets (hidden from the lg breakpoint, where the top
 * navbar has the full menu). Adds its own height to the page bottom padding through a class on <html>
 * so content and the footer are never covered.
 */
export default function MobileBottomNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useSelector((s) => s?.auth?.user);
  const subscribed = useSelector((s) => s?.subscriptions?.subscribed);

  const path = location.pathname || "/";
  const hidden = !user || !subscribed || HIDDEN_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix + "/") || path.startsWith(prefix + "?"));

  React.useEffect(() => {
    document.documentElement.classList.toggle("has-bottom-nav", !hidden);
    return () => document.documentElement.classList.remove("has-bottom-nav");
  }, [hidden]);

  if (hidden) return null;

  const viewer = isViewerMember(user);
  const supplier = String(user?.entity_type || user?.company?.entity_type || "").toLowerCase() === "supplier_entity";
  // Notifications already live in the top bar, so the middle slot is the entity's main action:
  // publishers create tenders, suppliers watch their invitations, viewers browse the premium list.
  const items = [
    { to: "/dashboard", label: t("dashboard"), icon: LayoutGrid, end: true },
    { to: "/tenders", label: t("All_Tenders"), icon: Home },
    viewer
      ? (COMPANIES_MODULE_HIDDEN ? null : { to: "/premium-subscribers", label: t("bottom_nav_premium"), icon: Star })
      : supplier
        ? { to: "/incoming-invitations", label: t("bottom_nav_invitations"), icon: Mail }
        : { to: "/tender-form", label: t("bottom_nav_create"), icon: Plus, accent: true },
    viewer
      ? null
      : supplier
        ? { to: "/my-offers", label: t("MyOffers"), icon: FileText }
        : { to: "/my-tenders", label: t("MyTenders"), icon: FileText },
    { to: "/profile", label: t("profile"), icon: User },
  ].filter(Boolean);

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85 lg:hidden dark:border-slate-700 dark:bg-slate-900/95"
      style={{ paddingBottom: "var(--safe-area-bottom, 0px)" }}
      aria-label={t("dashboard")}
    >
      <ul className="mx-auto flex max-w-xl items-stretch justify-around">
        {items.map(({ to, label, icon: Icon, end, accent }) => (
          <li key={to} className="min-w-0 flex-1">
            <NavLink
              to={to}
              end={end}
              title={label}
              aria-label={label}
              className={({ isActive }) =>
                cx(
                  "relative flex min-h-14 flex-col items-center justify-center gap-1 px-1 py-1.5 text-[11px] font-semibold leading-tight transition-colors",
                  isActive ? "text-brand" : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cx(
                      "relative flex h-7 w-11 items-center justify-center rounded-full transition-colors",
                      accent ? "bg-brand text-white shadow-sm" : isActive && "bg-brand-soft dark:bg-brand/15"
                    )}
                  >
                    <Icon className="size-5" strokeWidth={accent ? 2.6 : isActive ? 2.4 : 2} aria-hidden="true" />
                  </span>
                  <span className="max-w-full truncate max-[359px]:sr-only">{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
